import { GrowthProfileOverrideKind } from '@/types/schema';
import { UnreachableError } from '@/utils/errors';

import {
  CustomEntityGrowthCustomizationType,
  GrowthProfileModalGrowthOverride,
} from '../GrowthProfileModal/GrowthProfileModal.types';
import { CustomEntityGrowthModalProps } from './CustomEntityGrowthModal';
import {
  CustomEntityGrowthModal_EntityFragment,
  CustomEntityGrowthModal_ProposalFragment,
} from './graphql/CustomEntityGrowthModal.generated';

export function getCustomEntityGrowthModalCustomizationType({
  kind,
  entityGrowthBeforeFutureValuation,
  entityGrowthAfterFutureValuation,
  entityFutureValuationValue,
  entityFutureValuationYear,
  customGrowthRate,
}: Partial<GrowthProfileModalGrowthOverride>): CustomEntityGrowthCustomizationType | null {
  if (kind === GrowthProfileOverrideKind.Asset) {
    return CustomEntityGrowthCustomizationType.None;
  }

  if (entityGrowthBeforeFutureValuation && entityGrowthAfterFutureValuation) {
    return CustomEntityGrowthCustomizationType.GrowthRateAndFutureValuation;
  }

  if (entityFutureValuationValue && entityFutureValuationYear) {
    return CustomEntityGrowthCustomizationType.FutureValuation;
  }

  if (customGrowthRate) {
    return CustomEntityGrowthCustomizationType.GrowthRate;
  }

  return null;
}

export function handleCustomEntityGrowthModalSubmit({
  formData,
  entities,
  proposals,
  onSubmit,
}: {
  formData: GrowthProfileModalGrowthOverride;
  entities: CustomEntityGrowthModal_EntityFragment[];
  proposals: CustomEntityGrowthModal_ProposalFragment[];
  onSubmit: CustomEntityGrowthModalProps['onSubmit'];
}): { errorMessage: string } | undefined {
  let relevantEntity: CustomEntityGrowthModal_EntityFragment | undefined;
  let relevantProposal: CustomEntityGrowthModal_ProposalFragment | undefined;
  let displayName = 'Unknown';

  const isProposal = formData.kind === GrowthProfileOverrideKind.Proposal;

  if (isProposal) {
    const proposal = proposals.find(
      (p) => p.id === formData.entityOrProposalId
    );
    if (proposal) {
      relevantProposal = proposal;
      displayName = proposal.displayName || 'Unknown Proposal';
    }
  } else {
    const entity = entities.find((e) => e.id === formData.entityOrProposalId);
    if (entity) {
      relevantEntity = entity;
      displayName = entity.subtype?.displayName || 'Unknown Entity';
    }
  }

  if (!relevantEntity && !relevantProposal) {
    throw new Error(
      `Could not find entity or proposal with id ${formData.entityOrProposalId}`
    );
  }

  const commonFormData = {
    id: formData.id,
    customizationType: formData.customizationType,
    kind: formData.kind,
    displayName,
    entityOrProposalId: formData.entityOrProposalId,
  };

  switch (formData.customizationType) {
    case CustomEntityGrowthCustomizationType.GrowthRate:
      onSubmit({
        formData: {
          ...commonFormData,
          customGrowthRate: formData.customGrowthRate,
        },
        entity: relevantEntity,
        proposal: relevantProposal,
      });
      return;
    case CustomEntityGrowthCustomizationType.FutureValuation:
      onSubmit({
        formData: {
          ...commonFormData,
          entityFutureValuationValue: formData.entityFutureValuationValue,
          entityFutureValuationYear: formData.entityFutureValuationYear,
        },
        entity: relevantEntity,
        proposal: relevantProposal,
      });
      return;
    case CustomEntityGrowthCustomizationType.GrowthRateAndFutureValuation:
      onSubmit({
        formData: {
          ...commonFormData,
          entityFutureValuationValue: formData.entityFutureValuationValue,
          entityFutureValuationYear: formData.entityFutureValuationYear,
          entityGrowthAfterFutureValuation:
            formData.entityGrowthAfterFutureValuation,
          entityGrowthBeforeFutureValuation:
            formData.entityGrowthBeforeFutureValuation,
        },
        entity: relevantEntity,
        proposal: relevantProposal,
      });
      return;
    case CustomEntityGrowthCustomizationType.None:
      return {
        errorMessage: 'Please select a customization type',
      };
    default:
      throw new UnreachableError({
        case: formData.customizationType,
        message: 'Invalid customization type',
      });
  }
}
